import { JSX, ReactNode } from 'react';
import {
  APIAggregatesResponse,
  APIComponentAttributes,
  APIEntityType,
  APIFieldType,
  APIReportColumnSuggestions,
  APIReportFilters,
  APISearchResponse,
  ArdoqId,
  FilterExpression,
  FilterValue,
  Operator,
  ReportColumn,
  SortOrder,
} from '@ardoq/api-types';
import {
  ReportEventLocations,
  ReportReaderLocation,
  TrackEvent,
} from './tracking/types';
import { ComponentLinkHandler, GremlinPathItem } from '@ardoq/renderers';
import { Locale } from '@ardoq/locale';
import { ArdoqError, Maybe } from '@ardoq/common-helpers';
import { DropdownItem } from '@ardoq/dropdown-menu';
import { ListPopoverOptionProps } from '@ardoq/table-filter-bar';
import { EnhancedScopeData } from '@ardoq/data-model';
import { SelectOption } from '@ardoq/select';
import {
  CopyEntityURLButtonProps,
  NewFeatureNotificationProps,
} from '@ardoq/snowflakes';
import { SolutionMetadata } from '@ardoq/core';
import type { ReportReaderLabels } from './labels';
import type { I18nLocale } from '@ardoq/i18n';

export type ReportPagination = {
  from: number;
  size: number;
};

export type ReportPageAndPagination = {
  page: number;
  pagination: ReportPagination;
};

export type ReportSort = { columnKey: Maybe<string>; order: SortOrder };

export type SortFilterPagination = {
  sort: ReportSort;
  filters: APIReportFilters | null;
  pagination: ReportPagination;
  page: number;
};

export type ReportColumnSuggestionsFetchFn = (
  searchPhrase: string,
  columnKey: string
) => Promise<APIReportColumnSuggestions>;

export type ReportFilterDrawerCommands = {
  setPageAndPagination: (pageAndPagination: ReportPageAndPagination) => void;
  setSort: (sort: ReportSort) => void;
  clearAllFilters: VoidFunction;
};

type ReportFilterSidebarCommands = {
  addEmptyFilterExpression: (column: ReportColumn) => void;
  removeFilterExpression: (columnKey: ArdoqId) => void;
};

export type ReportFilterInputCommands = {
  setFilterExpression: (
    column: ReportColumn,
    expression: FilterExpression | FilterExpression[]
  ) => void;
  removeFilterExpression: (columnKey: ArdoqId) => void;
};

type ReportFilterAsyncInputCommands = {
  setFilterExpression: (
    column: ReportColumn,
    value: Maybe<FilterExpression[] | FilterExpression>
  ) => void;
  removeFilterExpression: (columnKey: ArdoqId) => void;
};

export type ReportFilterCommands = {
  loadComponentSuggestions: Maybe<ReportColumnSuggestionsFetchFn>;
} & ExportButtonCommands &
  ReportFilterDrawerCommands &
  ReportFilterInputCommands &
  ReportFilterAsyncInputCommands &
  ReportFilterSidebarCommands;

export type ReportReaderCommands = ExportButtonCommands & ReportFilterCommands;

export type ExportButtonCommands = {
  exportToExcel: VoidFunction;
  exportToReadableExcel: VoidFunction;
  exportToJSON: VoidFunction;
};

export type HasComponentReadPermission = (componentId: ArdoqId) => boolean;

export type EnhancedSearchResponse = APISearchResponse & {
  scopeData: Maybe<EnhancedScopeData>;
};

export type ExportButtonProps = {
  isDisabled: boolean;
  isExportToReadableExcelDisabled: boolean;
};

export type ReportNewFeatureNotificationProps = {
  show: boolean;
  onClose: VoidFunction;
};

export type AggregatesResultsState = {
  aggregateResults: Maybe<APIAggregatesResponse>;
  aggregateError: Maybe<ArdoqError>;
};

export type ReportReaderProps = {
  isEntityNavigationDisabled?: boolean;
  getComponentLinkHandler?: GetComponentLinkHandler;
  getComponentNavigationOptions?: GetComponentNavigationOptions;
  currentUserLocale: Locale;
  openHome?: VoidFunction;
  trackEvent: TrackEvent;
  reportReaderLocation: ReportReaderLocation;
  getHasComponentReadPermission?: (componentId: ArdoqId) => boolean;
  hasExcelExportFailed?: boolean;
  commands: ReportReaderCommands;
  addFilterButtonOptions: ListPopoverOptionProps[];
  globalDashboardFilterParameters?: DropdownItem[];
  sortFilterPagination: SortFilterPagination;
  searchResults: Maybe<EnhancedSearchResponse>;
  isSearchBeingExecuted: boolean;
  filterableColumns: ReportColumn[];
  isAggregatesBeingExecuted: boolean;
  reportId: ArdoqId;
  solutionMetadata?: SolutionMetadata;
  isExportingDisabled?: boolean;
  isCaptureMode?: boolean;
  filtersToRender: FilterInputProps[];
  newFeatureNotificationsProps?: NewFeatureNotificationProps[];
  aggregatesResultsState: AggregatesResultsState;
  isFilteringDisabled?: boolean;
  error:
    | ReportError.USER_IS_MISSING_READ_ACCESS_TO_REPORT
    | ReportError.REPORT_NOT_FOUND
    | ArdoqError
    | null;
  isLoading: boolean;
  aiButtonProps?: {
    isActive: boolean;
    onClick: VoidFunction;
  };
  labels?: ReportReaderLabels;
  i18nLocale: I18nLocale;
  /** Rendered above the report, in the scrollable header area. */
  children?: ReactNode;
} & (
  | {
      reportReaderLocation: ReportEventLocations.ARDOQ;
    }
  | {
      reportReaderLocation:
        | ReportEventLocations.DISCOVER
        | ReportEventLocations.PRESENTATION;
      canDrilldownIntoDiscover: boolean;
      exportButtonProps?: ExportButtonProps;
      copyUrlButtonProps?: CopyEntityURLButtonProps;
    }
);

export type GetComponentLinkHandler = (
  componentId: ArdoqId
) => ComponentLinkHandler;

export type GetComponentNavigationOptions = (
  component: APIComponentAttributes
) => DropdownItem[];

// TODO: calculated field types
// NOTE: missing user, multi select list and date range fields since sorting is not useful right now
export const sortableColumnTypes = [
  APIFieldType.TEXT,
  APIFieldType.TEXT_AREA,
  APIFieldType.DATE_ONLY,
  APIFieldType.DATE_TIME,
  APIFieldType.NUMBER,
  APIFieldType.URL,
  APIFieldType.EMAIL,
  APIFieldType.LIST,
  APIFieldType.CHECKBOX,
];

export enum ReportError {
  USER_CANNOT_CREATE_GREMLIN_AND_DATA_SOURCE_IS_GREMLIN = 'reportError/USER_CANNOT_CREATE_GREMLIN_AND_DATA_SOURCE_IS_GREMLIN',
  USER_IS_MISSING_WORKSPACE_ACCESS = 'reportError/USER_IS_MISSING_WORKSPACE_ACCESS',
  USER_IS_MISSING_READ_ACCESS_TO_REPORT = 'reportError/USER_IS_MISSING_READ_ACCESS',
  USER_IS_MISSING_EDIT_ACCESS_TO_REPORT = 'reportError/USER_IS_MISSING_EDIT_ACCESS',
  REPORT_NOT_FOUND = 'reportError/REPORT_NOT_FOUND',
}

export type DerivedDataForReferences =
  | ArdoqId[]
  | {
      incoming?: ArdoqId[];
      outgoing?: ArdoqId[];
    };

export type DataSourceRow = {
  [key: string]: unknown;
  _id: ArdoqId;
  name?: string;
  parent: ArdoqId | null;
  ardoq?: { 'entity-type': APIEntityType; redactedFields?: string[] };
  derivedData?: Record<string, GremlinPathItem[] | DerivedDataForReferences>;
  source?: ArdoqId;
  target?: ArdoqId;
};

export enum FilterInputType {
  NUMERIC = 'NUMERIC',
  DATE = 'DATE',
  ASYNC_MULTISELECT = 'ASYNC_MULTISELECT',
  CHECKBOX = 'CHECKBOX',
  TEXT = 'TEXT',
  EMAIL = 'EMAIL',
  VALUE_PRESENCE = 'VALUE_PRESENCE',
  UNKNOWN = 'UNKNOWN',
}

export type FilterInputProps = (
  | {
      inputType: FilterInputType.ASYNC_MULTISELECT;
      props: ColumnFilterMultiselectWithLoadSuggestionsProps;
    }
  | {
      inputType: FilterInputType.NUMERIC;
      props: FilterWithOperatorProps;
    }
  | {
      inputType: FilterInputType.DATE;
      props: FilterWithOperatorProps;
    }
  | {
      inputType: FilterInputType.CHECKBOX;
      props: CheckboxColumnFilterProps;
    }
  | {
      inputType: FilterInputType.TEXT;
      props: TextColumnFilterProps;
    }
  | {
      inputType: FilterInputType.EMAIL;
      props: TextColumnFilterProps;
    }
  | {
      inputType: FilterInputType.VALUE_PRESENCE;
      props: ValuePresenceColumnFilterProps;
    }
  | {
      inputType: FilterInputType.UNKNOWN;
      props: UnknownColumnFilterInputProps;
    }
) & { additionalBottomContent?: JSX.Element };

type EnhancedFilterExpression = FilterExpression & {
  formattedDisplayValue?: string | { start?: string; end?: string };
};

export type FilterWithOperatorProps = {
  expression: EnhancedFilterExpression;
  column: ReportColumn;
  isReadOnly?: boolean;
  excludedOperators?: Operator[];
  removeFilter: (columnKey: string) => void;
  changeFilterExpression: (
    column: ReportColumn,
    expression: FilterExpression | FilterExpression[]
  ) => void;
};

export type ColumnFilterMultiselectWithLoadSuggestionsProps = {
  column: ReportColumn;
  selected: Array<FilterExpression & { label: string }>;
  onValueChange: (value: SelectOption<FilterValue>[]) => void;
  loadSuggestions: ReportColumnSuggestionsFetchFn;
  removeFilter: (columnKey: string) => void;
  isReadOnly?: boolean;
};

export type TextColumnFilterProps = {
  column: ReportColumn;
  selected: Array<FilterExpression & { label: string }>;
  loadSuggestions: ReportColumnSuggestionsFetchFn;
  isReadOnly?: boolean;
  removeFilter: (columnKey: string) => void;
  changeFilterExpression: (
    column: ReportColumn,
    expression: FilterExpression | FilterExpression[]
  ) => void;
};

export type CheckboxColumnFilterProps = {
  column: ReportColumn;
  selected: FilterValue[];
  options: SelectOption<FilterValue>[];
  onValueChange: (value: FilterValue[] | null) => void;
  removeFilter: (columnKey: string) => void;
  isReadOnly?: boolean;
};

export type ValuePresenceColumnFilterProps = {
  column: ReportColumn;
  selected: FilterExpression[];
  isReadOnly?: boolean;
  removeFilter: (columnKey: string) => void;
  changeFilterExpression: (
    column: ReportColumn,
    expression: FilterExpression | FilterExpression[]
  ) => void;
};

type UnknownReportColumn = ReportColumn & { type: null };

export type UnknownColumnFilterInputProps = {
  column: UnknownReportColumn;
  operator: Operator | undefined;
  removeFilter: (columnKey: string) => void;
  value: string | string[] | { start: string; end: string } | undefined;
  isReadOnly?: boolean;
};

export type SearchResults = {
  scopeData: Maybe<EnhancedScopeData>;
  results: Array<Record<ReportColumn['key'], any>>;
  columns: ReportColumn[];
};
